import {
  Box,
  Button,
  Collapse,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { format, parseISO } from "date-fns";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addCustomer,
  searchCustomer,
  setCustomerDetails,
} from "../../reduxStore/slices/cartSlice";
import { useNotify } from "../../utils/snackbarUtils";
import AppButton from "../app-button/AppButton";
import AppDialog from "../app-dialog/AppDialog";

function CustomerInfoChangeDialog({
  isCustomerInfoChangeDialogOpen,
  onDialogCancel,
  customerDetails,
}) {
  const [showMore, setShowMore] = useState(false);

  const { showSuccess, handleApiError, showError } = useNotify();
  const dispatch = useDispatch();
  const { loading: isLoading, customerSearchResults: customerData } =
    useSelector((state) => state.cart);
  const { user } = useSelector((state) => state.auth);
  const storeId = user?.storeId;

  const [id, setId] = useState("");
  const [uid, setUid] = useState("");
  const [recordStatus, setRecordStatus] = useState("");
  const [customerMobileNo, setCustomerMobileNo] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [city, setCity] = useState("");
  const [address, setAddress] = useState("");
  const [dob, setDob] = useState(null);
  const [gender, setGender] = useState(null);
  const [isExistingCustomer, setIsExistingCustomer] = useState(false);
  const [isSelectCustomer, setIsSelectCustomer] = useState(false);
  const [isMobileChanged, setIsMobileChanged] = useState(false);

  useEffect(() => {
    if (
      customerData &&
      customerData.id &&
      customerMobileNo &&
      customerMobileNo.length === 10
    ) {
      // Existing customer → prefill form
      setId(customerData.id || "");
      setUid(customerData.uid || "");
      setRecordStatus(customerData.record_status || "");
      setCustomerMobileNo(customerData.mobile_no || "");
      setFirstName(customerData.first_name || "");
      setLastName(customerData.last_name || "");
      setEmail(customerData.email || "");
      setCity(customerData.city || "");
      setAddress(customerData.address || "");
      setDob(customerData.dob ? parseISO(customerData.dob) : null);
      setGender(customerData.gender || null);
      setIsExistingCustomer(true);
      setIsSelectCustomer(true);
    } else if (customerDetails && customerDetails.id && !isMobileChanged) {
      setId(customerDetails.id || "");
      setUid(customerDetails.uid || "");
      setRecordStatus(customerDetails.record_status || "");
      setCustomerMobileNo(customerDetails.mobile_no || "");
      setFirstName(customerDetails.first_name || "");
      setLastName(customerDetails.last_name || "");
      setEmail(customerDetails.email || "");
      setCity(customerDetails.city || "");
      setAddress(customerDetails.address || "");
      setDob(customerDetails.dob ? parseISO(customerDetails.dob) : null);
      setGender(customerDetails.gender || null);
      setIsExistingCustomer(true);
    } else {
      // No customer → reset form
      resetFields();
    }
  }, [customerData, customerDetails]);

  const resetFields = () => {
    setId("");
    setUid("");
    setRecordStatus("");
    setFirstName("");
    setLastName("");
    setEmail("");
    setCity("");
    setAddress("");
    setDob(null);
    setGender(null);
    setIsExistingCustomer(false);
    setIsSelectCustomer(false);
  };

  const formattedDob = dob ? format(dob, "yyyy-MM-dd") : null;

  const handleMobileChange = async (e) => {
    const value = e.target.value.replace(/\D/g, "");
    setCustomerMobileNo(value);
    setIsMobileChanged(true);
    resetFields();

    if (value.length === 10) {
      try {
        await dispatch(searchCustomer({ mobileNo: value })).unwrap();
      } catch (err) {
        console.log("No customer found, can create new.");
      }
    }
  };

  const customerValidations = () => {
    if (!customerMobileNo) {
      showError("Please enter customer Mobile No.");
      return false;
    }

    if (customerMobileNo.length !== 10) {
      showError("Mobile number must be 10 digits.");
      return false;
    }

    if (!firstName) {
      showError("Please enter customer name.");
      return false;
    }
    return true;
  };

  // Save Customer (POST)
  const handleSaveCustomer = async () => {
    if (!customerValidations()) return;
    if (customerData && customerData.mobile_no) {
      dispatch(setCustomerDetails(customerData));
      onDialogCancel();
    } else {
      try {
        const obj = {
          id,
          uid,
          record_status: recordStatus,
          mobile_no: customerMobileNo,
          first_name: firstName,
          last_name: lastName,
          email,
          city,
          address,
          dob: formattedDob,
          gender,
        };

        const res = await dispatch(addCustomer(obj)).unwrap();
        onDialogCancel();
        showSuccess(res.message);
        obj["id"] = res.id;
        dispatch(setCustomerDetails(obj));
      } catch (err) {
        handleApiError(err);
      }
    }
  };

  const dialogCustomerInfoChangeFooter = (
    <Box sx={{ display: "flex", gap: 2 }}>
      <Button size="medium" onClick={onDialogCancel}>
        Cancel
      </Button>
      <AppButton
        disabled={isLoading || customerDetails?.mobile_no === customerMobileNo}
        isLoading={isLoading}
        fullWidth
        size="medium"
        variant="contained"
        onClick={handleSaveCustomer}
      >
        {isSelectCustomer && customerDetails?.mobile_no !== customerMobileNo
          ? "Select"
          : "Save Changes"}
      </AppButton>
    </Box>
  );

  return (
    <AppDialog
      maxWidth="xs"
      isOpen={isCustomerInfoChangeDialogOpen}
      id={"customer_info_change"}
      title={
        <>{customerDetails?.id ? "Edit Customer Info" : "Add Customer Info"}</>
      }
      footer={dialogCustomerInfoChangeFooter}
    >
      <Box sx={{ m: "20px 5px" }}>
        <Stack spacing={3} sx={{ mt: 2, mb: 2 }}>
          <TextField
            placeholder="Enter customer Mobile No."
            autoFocus
            label="Customer Mobile No."
            fullWidth
            autoComplete="T-customer-mobile"
            name="customer_mobile_no"
            type="number"
            value={customerMobileNo}
            onChange={handleMobileChange}
          />
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              autoComplete="T-first-name"
              label="First Name *"
              variant="outlined"
              fullWidth
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={isExistingCustomer}
            />
            <TextField
              autoComplete="T-last-name"
              label="Last Name (Optional)"
              variant="outlined"
              fullWidth
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={isExistingCustomer}
            />
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              autoComplete="T-email"
              label="Email ID (Optional)"
              variant="outlined"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isExistingCustomer}
            />
            <TextField
              autoComplete="T-city"
              label="City (Optional)"
              variant="outlined"
              fullWidth
              value={city}
              onChange={(e) => setCity(e.target.value)}
              disabled={isExistingCustomer}
            />
          </Box>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => setShowMore(!showMore)}
          >
            {showMore ? "Hide Details" : "More Details"}
          </Button>

          <Collapse in={showMore}>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <TextField
                autoComplete="T-address"
                fullWidth
                label="Address (Optional)"
                variant="outlined"
                multiline
                rows={2}
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                disabled={isExistingCustomer}
              />
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  disabled={isExistingCustomer}
                  label="Date of Birth (Optional)"
                  value={dob}
                  onChange={(newValue) => setDob(newValue)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
              <TextField
                select
                fullWidth
                label="Gender (Optional)"
                variant="outlined"
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                disabled={isExistingCustomer}
              >
                <MenuItem value="">Select</MenuItem>
                <MenuItem value="male">Male</MenuItem>
                <MenuItem value="female">Female</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </TextField>
            </Stack>
          </Collapse>
        </Stack>
      </Box>
    </AppDialog>
  );
}

export default CustomerInfoChangeDialog;
